/**
 * GlobalSeoFallback — emits canonical + hreflang + og:locale tags for ANY
 * route that does NOT call useSeoHead itself. Mounted once in Layout so it
 * runs after every route change.
 *
 * Effect ordering guarantees:
 *   1. Old page's useSeoHead cleanup runs (on unmount)
 *   2. New page mounts; if it calls useSeoHead, its effect runs FIRST (child)
 *   3. This fallback's effect runs LATER (parent in Layout)
 *      → it checks for [data-seo-managed] tags and SKIPS if already handled
 *      → otherwise it injects the baseline alternates
 *
 * This guarantees no duplicate hreflang tags, no missing reciprocal links,
 * and exactly one canonical URL per route — across all 1.290 indexed URLs.
 */
import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { buildSeoUrls } from "../i18n/routing";

const OG_LOCALE_MAP = {
  nl: "nl_NL", en: "en_GB", de: "de_DE", fr: "fr_FR", es: "es_ES",
  it: "it_IT", pl: "pl_PL", pt: "pt_PT", sv: "sv_SE", da: "da_DK",
};

// Routes that must NOT be indexed (admin, distributor portals, cart/checkout)
const NOINDEX_PREFIXES = [
  "/admin",
  "/distributor",
  "/cart",
  "/checkout",
  "/order-success",
];

function shouldNoIndex(pathname) {
  return NOINDEX_PREFIXES.some((p) => pathname.startsWith(p));
}

export default function GlobalSeoFallback() {
  const location = useLocation();

  useEffect(() => {
    if (typeof document === "undefined") return;
    const head = document.head;

    // Skip if a page-level useSeoHead already manages SEO for this route.
    if (head.querySelector('link[rel="alternate"][hreflang][data-seo-managed="true"]')) {
      return;
    }

    const pathname = location.pathname || "/";
    const isNoIndex = shouldNoIndex(pathname);

    // Derive lang directly from URL — robust against i18n.language race
    // conditions during navigation.
    const urlLangMatch = pathname.match(/^\/(en|de|fr|es|it|pl|pt|sv|da)(\/|$)/i);
    const lang = urlLangMatch ? urlLangMatch[1].toLowerCase() : "nl";

    // Clean up any previous fallback-managed tags
    head.querySelectorAll('[data-seo-fallback="true"]').forEach((el) => el.remove());

    // Robots meta for noindex routes
    if (isNoIndex) {
      const robots = document.createElement("meta");
      robots.setAttribute("name", "robots");
      robots.setAttribute("content", "noindex, nofollow");
      robots.dataset.seoFallback = "true";
      head.appendChild(robots);
      return; // No canonical/hreflang for noindex routes
    }

    const { canonical, alternates } = buildSeoUrls(pathname, lang);

    // Remove any stale canonical / alternate links that are NOT managed by
    // useSeoHead (we identified earlier that none exist).
    head.querySelectorAll('link[rel="canonical"]:not([data-seo-managed]), link[rel="alternate"][hreflang]:not([data-seo-managed])').forEach((el) => el.remove());

    // Canonical
    const linkCanonical = document.createElement("link");
    linkCanonical.setAttribute("rel", "canonical");
    linkCanonical.setAttribute("href", canonical);
    linkCanonical.dataset.seoFallback = "true";
    head.appendChild(linkCanonical);

    // hreflang × 10 + x-default
    alternates.forEach(({ hreflang, href }) => {
      const a = document.createElement("link");
      a.setAttribute("rel", "alternate");
      a.setAttribute("hreflang", hreflang);
      a.setAttribute("href", href);
      a.dataset.seoFallback = "true";
      head.appendChild(a);
    });

    // og:locale + og:locale:alternate
    const ogLocale = document.createElement("meta");
    ogLocale.setAttribute("property", "og:locale");
    ogLocale.setAttribute("content", OG_LOCALE_MAP[lang] || "nl_NL");
    ogLocale.dataset.seoFallback = "true";
    head.appendChild(ogLocale);

    alternates.forEach(({ hreflang }) => {
      if (hreflang === "x-default" || hreflang === lang) return;
      const og = document.createElement("meta");
      og.setAttribute("property", "og:locale:alternate");
      og.setAttribute("content", OG_LOCALE_MAP[hreflang] || hreflang);
      og.dataset.seoFallback = "true";
      head.appendChild(og);
    });

    // og:url should match canonical
    const ogUrl = document.createElement("meta");
    ogUrl.setAttribute("property", "og:url");
    ogUrl.setAttribute("content", canonical);
    ogUrl.dataset.seoFallback = "true";
    head.appendChild(ogUrl);
  }, [location.pathname]);

  return null;
}
